import { normalizedChannelsMap } from '../../src/utils/normalizedChannelsMap.js'
import { widgetManager } from '../../src/widgetManager/index.js'
import ScrollFixationDetector from './scroll-fixation-detector'
import cookieConsentManager from './cookie-consent-manager'
import TrueengageLogger from './trueengage-logger'
import { MetricType } from '../utils/metric-type'
import FixationDetector from './fixation-detector'
import debounce from '../helpers/debounce'
import Queue from '../models/queue'
const logger = new TrueengageLogger()

// Set the time window duration (1 minute)
const MetricsTimeWindow = 20 * 1000 // 60 seconds * 1000 milliseconds
const MaxInactivityTime = 5 * 60 * 1000
const CookieExpiration = 5
const MaxMetricsQueueLength = 3

export default class MetricsCollectorService {
  constructor({ host, statisticsHost, accountId, widgetId, visitorId, availableChannels, country, mobile, language }, authToken) {
    this.token = authToken

    this.host = host
    this.statisticsHost = statisticsHost
    this.accountId = accountId
    this.widgetId = widgetId
    this.visitorId = visitorId
    this.widgetLanguage = language

    this.metrics = {}
    this.widgetMetrics = {}
    this.widgetMetricsQueue = new Queue()

    this.headsupMetrics = []
    if (this.readCookie('headsupMetrics', false)) {
      this.headsupMetrics = JSON.parse(this.readCookie('headsupMetrics', false))
    }

    this.currentHeadsup = null

    let cachedMetrics = null
    if ((cachedMetrics = this.readCookie('trueengage_metrics'))) {
      this.widgetMetricsQueue = new Queue(cachedMetrics)
    }

    let cachedWidgetMetrics = null
    if ((cachedWidgetMetrics = this.readCookie('trueengage_widget_metrics', false))) {
      this.widgetMetrics = JSON.parse(cachedWidgetMetrics)
    }

    this.helperMetrics = {}
    this.widgetHelperMetrics = {}

    this.sessionMetrics = {}
    this.sessionMetrics['lastActionTime'] = Date.now()

    this.availableChannels = availableChannels

    this.sessionStartTime = this.setInitSessionDate()
    this.currentPageStartTime = Date.now()

    this.totalIdleTime = 0
    this.previousScrollY = window.scrollY

    this.firstPeriod = true

    this.visitorCountry = country
    this.visitorDevice = mobile ? 'mobile' : 'desktop'

    this.setWidgetState('icon')

    this.userActive = true

    this.fixationDetector = new FixationDetector(this)
    this.scrollFixationDetector = new ScrollFixationDetector(this)

    this.mlWhenModel = 'customer'
    this.mlWhatModel = 'customer'
    this.mlSentenceHash = ''

    this.widgetInteraction = false

    this.intervalListeners = []

    this.windowActive = true

    this.tabId = this.generateTabId()
    this.setCurrentTabActive()

    this.intervalListenerCounter = 0 // Add this line to initialize the counter

    this.visitorSessionId = this.generateVisitorSessionId()
    this.setSessionProbing(this.visitorSessionId)
  }

  setSessionProbing(visitorSessionId) {
    if (localStorage.getItem('trueengage-session-probing')) {
      this.sessionProbingFlag = localStorage.getItem('trueengage-session-probing') === 'true'
      return
    }
    const probingFlag = parseInt(visitorSessionId.substring(0, 2), 16) % 100
    this.sessionProbingFlag = probingFlag < 5
    logger.debug(`[setSessionProbing] Probing flag: ${this.sessionProbingFlag}`)
  }

  generateVisitorSessionId() {
    let visitorSessionId = this.readCookie('visitorSessionId')
    if (!visitorSessionId) {
      visitorSessionId = this.uuidv4()
      this.setCookie('visitorSessionId', visitorSessionId, CookieExpiration)
    }
    return visitorSessionId
  }

  getCurrentMetrics() {
    const currentTime = Date.now()
    const sessionDuration = Math.round((currentTime - this.sessionStartTime) / 1000)
    const visitTime = currentTime - this.currentPageStartTime

    const totalVisitTime = (parseInt(this.readCookie('totalVisitTime', false)) || 0) + visitTime

    this.pushMetric('session_duration', MetricType.Max, sessionDuration)
    this.pushMetric('total_visit_time', MetricType.Max, Math.round(totalVisitTime / 1000))
    return this.metrics
  }

  generateTabId() {
    // Check if we already have a tab ID for this session
    let tabId = sessionStorage.getItem('tabId')

    if (!tabId) {
      // If not, generate a new one
      tabId = Date.now() + '-' + Math.random().toString(36).substring(2, 11)
      sessionStorage.setItem('tabId', tabId)
    }
    return tabId
  }

  isCurrentTabActive() {
    const isActive = localStorage.getItem('trueengage-activetabid') === this.tabId
    logger.debug(`TabID: ${this.tabId} isActive: ${isActive}`)
    return isActive
  }

  setCurrentTabActive() {
    cookieConsentManager.setLocalStorageItem('trueengage-activetabid', this.tabId, 'statistics')
  }

  setWidgetState(state) {
    //headsup, icon, widget
    switch (state) {
      case 'icon':
        this.widgetState = 'trueengage:icon'
        this.widgetInteraction = false
        break
      case 'widget':
        this.widgetState = 'trueengage:widget'
        this.widgetInteraction = true
        break
      case 'headsup':
        this.widgetState = 'trueengage:headsup'
        this.widgetInteraction = true
        break
    }
  }

  setInitSessionDate() {
    let initSessionDate = this.readCookie('sessionStart')
    if (initSessionDate !== null) {
      return parseInt(initSessionDate)
    } else {
      this.setCookie('sessionStart', Date.now(), CookieExpiration)
      return Date.now()
    }
  }

  setUserActive(isActive) {
    this.userActive = isActive
  }

  resetMetrics() {
    this.metrics = {}
    this.helperMetrics = {}
  }

  resetWidgetMetrics() {
    this.widgetMetrics = {}
    this.widgetMetricsQueue = new Queue()
    this.widgetHelperMetrics = {}
  }

  processMetrics(metrics = null, type = 'general', skipReset = false) {
    if (type === 'general' && this.widgetInteraction) return

    if (this.userActive && this.isCurrentTabActive()) {
      let measures = this.metrics
      if (metrics !== null) measures = metrics

      if (Object.keys(measures).length === 0) return

      let metricObject = {
        account_id: this.accountId,
        widget_id: this.widgetId,
        visitor_id: this.visitorId,
        available_channels: this.availableChannels,
        activity_history: [
          {
            timestamp: new Date().toISOString(),
            url: window.location.origin + window.location.pathname,
            ml_when_model_name: this.mlWhenModel,
            ml_what_model_name: this.mlWhatModel,
            sentence_hash: this.mlSentenceHash,
            measures: measures,
          },
        ],
      }

      this.addVisitorProfile(type)

      if (type === 'general') {
        this.widgetMetricsQueue.enqueue(metricObject.activity_history[0])

        if (this.widgetMetricsQueue.length > MaxMetricsQueueLength) {
          this.widgetMetricsQueue.dequeue()
        }

        this.setCookie('trueengage_metrics', JSON.stringify(this.widgetMetricsQueue), CookieExpiration)
      } else {
        this.updateSessionDuration(type)

        this.runIntervalListeners(true)

        metricObject.activity_history = this.buildHistoryObject(metricObject)

        //send metrics only after widget or headsup close
        this.sendMetrics(metricObject, () => {}).then((resp) => console.info(resp))
      }
    }

    if (type === 'general') this.resetMetrics()
    else if (type === 'widget') this.resetWidgetMetrics()
  }

  pushMetric(metric, type, value) {
    if (value === null || value === undefined) return false

    if (type === MetricType.Single) {
      this.metrics[metric] = value
    } else if (type === MetricType.Sum) {
      if (metric in this.metrics) this.metrics[metric] += value
      else this.metrics[metric] = value
    } else if (type === MetricType.Max) {
      if (metric in this.metrics && value < this.metrics[metric]) return
      else this.metrics[metric] = value
    } else if (type === MetricType.Avg) {
      if (!(metric in this.helperMetrics)) {
        this.helperMetrics[metric] = [value]
      } else {
        this.helperMetrics[metric].push(value)
      }
      let average = this.helperMetrics[metric].reduce((a, b) => a + b, 0) / this.helperMetrics[metric].length
      this.metrics[metric] = parseFloat(average.toFixed(2))
    } else if (type === MetricType.Bool && value) {
      this.metrics[metric] = value
    } else if (type === MetricType.Set) {
      if (!(metric in this.helperMetrics)) this.helperMetrics[metric] = new Set()
      this.helperMetrics[metric].add(value)

      this.metrics[metric] = JSON.stringify(Array.from(this.helperMetrics[metric]))
    }

    return true
  }

  pushWidgetMetric(metric, type, value) {
    logger.debug(`[metric] Widget metric ${metric}=${value}`)

    if (value === null || value === undefined) return false

    if (type === MetricType.Single) {
      this.widgetMetrics[metric] = value
    } else if (type === MetricType.Sum) {
      if (metric in this.widgetMetrics) this.widgetMetrics[metric] += value
      else this.widgetMetrics[metric] = value
    } else if (type === MetricType.Max) {
      if (metric in this.widgetMetrics && value < this.widgetMetrics[metric]) return
      else this.widgetMetrics[metric] = value
    } else if (type === MetricType.Avg) {
      if (!(metric in this.widgetHelperMetrics)) {
        this.widgetHelperMetrics[metric] = [value]
      } else {
        this.widgetHelperMetrics[metric].push(value)
      }
      let average = this.widgetHelperMetrics[metric].reduce((a, b) => a + b, 0) / this.widgetHelperMetrics[metric].length
      this.widgetHelperMetrics[metric] = parseFloat(average.toFixed(2))
    } else if (type === MetricType.Bool && value) {
      this.widgetMetrics[metric] = value
    } else if (type === MetricType.Set) {
      if (!(metric in this.widgetHelperMetrics)) this.widgetHelperMetrics[metric] = new Set()
      this.widgetHelperMetrics[metric].add(value)

      this.widgetMetrics[metric] = JSON.stringify(Array.from(this.widgetHelperMetrics[metric]))
    }

    this.setCookie('trueengage_widget_metrics', JSON.stringify(this.widgetMetrics), CookieExpiration, false)

    return true
  }

  //TODO reset activity time?
  calculateCurrentMetrics(resetActivityTime = true) {
    let pageReturns = this.getPageReturns(CookieExpiration, false)
    let pageReplays = this.getPageReplays()
    let headsupShowCount = parseInt(this.readCookie('headsupShowCount') || 0)
    let clipboardEvents = parseInt(this.readCookie('clipboardEvents') || 0)

    this.pushMetric('page_returns', MetricType.Max, pageReturns)
    this.pushMetric('page_replays', MetricType.Max, pageReplays)
    this.pushMetric('widget_state', MetricType.Single, this.widgetState)

    this.pushMetric('headsup_show_count', MetricType.Max, headsupShowCount)
    this.pushMetric('clipboard_count', MetricType.Max, clipboardEvents)

    this.calculateScrollRatioMax()

    let now = Date.now()
    let idleTime = now - this.sessionMetrics['lastActionTime']
    if (idleTime !== null && idleTime > 50) {
      logger.debug(`[CalculateCurrentMetrics] Idle time: ${idleTime}`)
      this.pushMetric('idle_time', MetricType.Avg, Math.round(idleTime / 1000))
      logger.debug(`[CalculateCurrentMetrics] AVG Idle time: ${this.metrics['idle_time']}`)
    }

    this.calculateVisitTime(resetActivityTime)
  }

  calculateScrollRatioMax() {
    let scrollTop = window.scrollY
    let docHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight
    )
    let winHeight = window.innerHeight || document.documentElement.clientHeight || document.getElementsByTagName('body')[0].clientHeight
    let scrollPercent = scrollTop / (docHeight - winHeight)
    let scrollPercentRounded = Math.round(scrollPercent * 100)
    let scrollRatio = Math.min(scrollPercentRounded, 100)

    this.pushMetric('scroll_ratio_max', MetricType.Max, scrollRatio)
  }

  attachWidgetEventListeners() {
    document.addEventListener('trueengage:widget_opened', (e) => {
      let data = e.detail
      let timestamp = e.timeStamp

      this.trueEngageWidgetOpenTime = timestamp

      this.setWidgetState('widget')

      this.calculateCurrentMetrics()

      //if the user opens widget when headsup is opened, widgetMetrics will be filled
      if (Object.keys(this.widgetMetrics).length === 0) {
        this.widgetMetrics = Object.assign({}, this.metrics)
      }

      this.pushWidgetMetric('trueengage_actions', MetricType.Set, e.type)
      this.pushWidgetMetric('widget_interaction_start_time', MetricType.Max, Date.now())
    })

    document.addEventListener('trueengage:widget_closed', (e) => {
      let data = e.detail
      let timestamp = e.timeStamp

      let time = Math.round((timestamp - this.trueEngageWidgetOpenTime) / 1000)
      logger.debug(`Widget closed after ${time} seconds`)

      this.setWidgetState('icon')
      this.saveHeadsupAttributes()

      this.pushWidgetMetric('trueengage_actions', MetricType.Set, e.type)
      this.pushWidgetMetric('widget_closed_time', MetricType.Max, time)

      logger.debug(`Sending widget metrics`)
      this.processMetrics(this.widgetMetrics, 'widget')
    })

    document.addEventListener('trueengage:widget_tab_changed', (e) => {
      const { previous_widget_tab, widget_visibility } = e.detail
      if (!widget_visibility) return

      const currentChannel = normalizedChannelsMap[widgetManager.getWidgetChannelInfo().channel]
      const previousChannel = normalizedChannelsMap[widgetManager.getWidgetChannelInfo(previous_widget_tab).channel]
      const isSamePrefix = currentChannel === previousChannel

      if (!currentChannel || isSamePrefix) return

      const action = `trueengage:channel_click_${currentChannel}`
      this.pushWidgetMetric('trueengage_actions', MetricType.Set, action)
    })

    document.addEventListener('trueengage:widget_business_success', (e) => {
      console.log('trigger public event', 'widget_business_success', 'confirmed:', e.detail.confirmed, 'state:', e.detail.state)
      let timestamp = e.timeStamp
      let time = Math.round((timestamp - this.trueEngageWidgetOpenTime) / 1000)
      this.pushWidgetMetric('widget_decision_time', MetricType.Max, time)

      const channel = widgetManager.getWidgetChannelInfo().channel
      let channelStartAction = `trueengage:communication_started_${normalizedChannelsMap[channel]}`
      this.pushWidgetMetric('trueengage_actions', MetricType.Set, channelStartAction)

      if(e.detail.confirmed){
        const businessSuccessAction = `trueengage:widget_business_success_confirmed`
        this.pushWidgetMetric('trueengage_actions', MetricType.Set, businessSuccessAction)
      }

      let widgetConversionCount = parseInt(this.readCookie('widgetConversion') || 0)
      widgetConversionCount += 1
      this.pushWidgetMetric('widget_conversion', MetricType.Max, widgetConversionCount)
      this.setCookie('widgetConversion', widgetConversionCount, CookieExpiration)
    })

    document.addEventListener('trueengage:message_sent', (e) => {})

    document.addEventListener('trueengage:headsup_opened', (e) => {
      let data = e.detail
      let timestamp = e.timeStamp

      this.trueEngageHeadsupOpenTime = timestamp
      const currentTime = Date.now()

      this.calculateCurrentMetrics()

      this.widgetMetrics = Object.assign({}, this.metrics)
      this.pushWidgetMetric('trueengage_actions', MetricType.Set, e.type)
      this.pushWidgetMetric('widget_interaction_start_time', MetricType.Single, currentTime)

      let headsupShowCount = parseInt(this.readCookie('headsupShowCount') || 0)
      headsupShowCount += 1

      this.setWidgetState('headsup')

      this.pushMetric('headsup_show_count', MetricType.Max, headsupShowCount)
      this.setCookie('headsupShowCount', headsupShowCount, CookieExpiration)

      const sessionTime = this.getSessionTime()
      this.currentHeadsup = {}
      this.currentHeadsup['engagement_start_session_time'] = sessionTime
      this.currentHeadsup['engagement_end_session_time'] = sessionTime
      this.currentHeadsup['sentence_hash'] = this.mlSentenceHash
      this.currentHeadsup['url'] = window.location.origin + window.location.pathname

      this.currentHeadsup['engagement_end_session_time'] = this.getSessionTime()
      this.headsupMetrics.push(this.currentHeadsup)
      logger.debug(`Headsup metrics: ${this.headsupMetrics}`)
      this.setCookie('headsupMetrics', JSON.stringify(this.headsupMetrics), CookieExpiration, false)
    })

    document.addEventListener('trueengage:engagement_headsup', (e) => {
      this.pushWidgetMetric('trueengage_actions', MetricType.Set, e.type)
    })

    document.addEventListener('trueengage:engagement_widget', (e) => {
      this.pushWidgetMetric('trueengage_actions', MetricType.Set, e.type)
    })

    document.addEventListener('trueengage:headsup_closed', (e) => {
      let data = e.detail
      let timestamp = e.timeStamp

      let time = Math.round((timestamp - this.trueEngageHeadsupOpenTime) / 1000)
      logger.debug(`Headsup closed after ${time} seconds`)

      this.setWidgetState('icon')

      if (!isNaN(time)) {
        this.pushWidgetMetric('headsup_display_time', MetricType.Max, time)
      }
      if (data.closedByUser) {
        this.saveHeadsupAttributes()

        this.pushWidgetMetric('trueengage_actions', MetricType.Set, e.type)
        this.processMetrics(this.widgetMetrics, 'widget')
      }
    })
  }

  // Attach event listeners to relevant events
  attachEventListeners() {
    window.addEventListener('beforeunload', (e) => {
      this.calculateVisitTime()
      this.updateSessionDuration()
      this.calculateCurrentMetrics()
      this.processMetrics()
    })

    document.addEventListener('click', () => {
      this.pushMetric('click_count', MetricType.Sum, 1)

      this.setUserActive(true)
    })

    window.addEventListener('focus', () => {
      this.setCurrentTabActive()

      if (this.windowActive) return

      const cookieName = 'visitorHistory'
      let visitHistory = []
      const visitCookie = this.readCookie(cookieName)
      if (visitCookie) {
        visitHistory = JSON.parse(visitCookie)
      }
      visitHistory.push(window.location.href)
      this.setCookie(cookieName, JSON.stringify(visitHistory), CookieExpiration)
    })

    document.addEventListener('copy', () => {
      let clipboardEvents = parseInt(this.readCookie('clipboardEvents') || 0)
      clipboardEvents += 1
      this.pushMetric('clipboard_count', MetricType.Max, clipboardEvents)
      this.setCookie('clipboardEvents', clipboardEvents, CookieExpiration)

      this.setUserActive(true)
    })

    document.addEventListener(
      'scroll',
      debounce(() => {
        let docHeight = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.offsetHeight,
          document.body.clientHeight,
          document.documentElement.clientHeight
        )
        let winHeight = window.innerHeight || document.documentElement.clientHeight || document.getElementsByTagName('body')[0].clientHeight

        let distanceScrolled = Math.abs(window.scrollY - this.previousScrollY) // Take the absolute value to account for both up and down scrolls
        this.previousScrollY = window.scrollY
        const scrollDistanceValue = parseFloat((distanceScrolled / (docHeight - winHeight)).toFixed(2))
        this.pushMetric('scroll_distance', MetricType.Sum, scrollDistanceValue)

        this.calculateScrollRatioMax()

        this.setUserActive(true)
      }, 300)
    )

    let measureMouseMovement = () => {
      let totalDistance = 0
      let prevX = 0
      let prevY = 0

      function calculateDistance(x, y) {
        const dx = x - prevX
        const dy = y - prevY

        const screenWidth = screen.width
        const screenHeight = screen.height
        const screenDiagonal = Math.sqrt(screenWidth * screenWidth + screenHeight * screenHeight)

        const distance = Math.sqrt(dx * dx + dy * dy)

        const normalizedDistance = Math.round((distance / screenDiagonal) * 100)
        return normalizedDistance
      }

      let handleMouseMove = (event) => {
        const { clientX, clientY } = event
        const distance = calculateDistance(clientX, clientY)
        prevX = clientX
        prevY = clientY

        this.setUserActive(true)
        this.pushMetric('mouse_movement', MetricType.Sum, distance)
      }

      document.addEventListener('mousemove', debounce(handleMouseMove, 300))
    }

    measureMouseMovement()

    //exit intent
    window.addEventListener('blur', (e) => {
      this.pushMetric('exit_intent', MetricType.Bool, true)
      this.windowActive = false

      this.setUserActive(true)
    })

    //interaction with text
    document.addEventListener('input', (event) => {
      this.pushMetric('text_interaction', MetricType.Bool, true)

      this.setUserActive(true)
    })

    //click decision delay
    // Attach event listener to track cursor position
    document.addEventListener('mousemove', (event) => {
      this.helperMetrics['cursorPositionTime'] = Date.now()
    })
    // Attach event listener to track click events
    document.addEventListener('click', (event) => {
      const clickTime = Date.now()
      if ('cursorPositionTime' in this.helperMetrics) {
        let clickDecisionDelay = clickTime - this.helperMetrics['cursorPositionTime']
        this.pushMetric('click_decision_delay', MetricType.Max, Math.round(clickDecisionDelay))
      }
    })

    let idleTimeMonitor = () => {
      let now = Date.now()
      if (!('lastActionTime' in this.sessionMetrics)) {
        this.sessionMetrics['lastActionTime'] = now
      }
      let idleTime = now - this.sessionMetrics['lastActionTime']
      this.sessionMetrics['lastActionTime'] = Date.now()

      if (idleTime === null) return

      this.totalIdleTime = 0

      logger.debug(`[IdleTimeMonitor] Idle time: ${idleTime}`)

      if (this.widgetInteraction) return

      this.pushMetric('idle_time', MetricType.Avg, Math.round(idleTime / 1000))
      logger.debug(`[IdleTimeMonitor] AVG Idle time: ${this.metrics['idle_time']}`)
    }

    //idle time
    document.addEventListener('input', debounce(idleTimeMonitor, 300))
    document.addEventListener('click', debounce(idleTimeMonitor, 300))
    document.addEventListener('scroll', debounce(idleTimeMonitor, 300))
    document.addEventListener('mousemove', debounce(idleTimeMonitor, 300))
  }

  // Metric: Page Returns
  getPageReturns(timeWindow, addToHistory = false) {
    const cookieName = 'visitorHistory'
    const maxPagesAhead = 2
    let _this = this

    // Count the number of times a user entered a page, ran a maximum of 2 pages ahead, and then returned to the initial page within the time window

    let pageReturns = 0
    const visitHistory = getVisitHistory()

    function getVisitHistory() {
      const visitCookie = _this.readCookie(cookieName)
      if (visitCookie) {
        return JSON.parse(visitCookie)
      } else {
        return []
      }
    }

    function setVisitHistory(history) {
      _this.setCookie(cookieName, JSON.stringify(history), timeWindow)
    }

    function trackPageVisit(page) {
      visitHistory.push(page)

      // Loop through the visit history
      for (let i = 0; i < visitHistory.length; i++) {
        const currentPage = visitHistory[i]
        let numPagesAhead = 0
        let j = i + 1

        // Count the number of pages ahead until reaching the maximum limit
        while (j < visitHistory.length && numPagesAhead < maxPagesAhead) {
          if (visitHistory[j] === currentPage) {
            pageReturns++
            break
          }
          numPagesAhead++
          j++
        }
      }

      if (addToHistory) setVisitHistory(visitHistory)
    }
    trackPageVisit(window.location.href)

    return pageReturns
  }

  // Metric: Page replays
  getPageReplays() {
    const cookieName = 'visitorHistory'
    let _this = this

    const visitHistory = getVisitHistory()

    function getVisitHistory() {
      const visitCookie = _this.readCookie(cookieName)
      if (visitCookie) {
        return JSON.parse(visitCookie)
      } else {
        return []
      }
    }

    let countMap = {}
    // Count the occurrences of each element
    for (let i = 0; i < visitHistory.length; i++) {
      const element = visitHistory[i]
      countMap[element] = (countMap[element] || 0) + 1
    }

    return Object.values(countMap).reduce((accumulator, currentValue) => accumulator + (currentValue - 1), 0)
  }

  // Total visit time
  calculateVisitTime(resetActivityTime = true) {
    const visitTime = Date.now() - this.currentPageStartTime
    logger.debug(`Visit time: ${visitTime}`)
    const totalVisitTime = (parseInt(this.readCookie('totalVisitTime', false)) || 0) + visitTime
    this.setCookie('totalVisitTime', totalVisitTime, 60 * 24 * 7, false)

    if (resetActivityTime) {
      this.currentPageStartTime = Date.now()
    }

    if (this.totalIdleTime > MaxInactivityTime) {
      return
    }

    this.pushMetric('total_visit_time', MetricType.Max, Math.round(totalVisitTime / 1000))
    this.pushMetric('activity_time_interval', MetricType.Max, Math.round(visitTime / 1000))
    return totalVisitTime
  }
  measureTotalVisitTime() {
    return Math.round(this.readCookie('totalVisitTime', false) / 1000) || 0
  }

  updateSessionDuration(type = 'general') {
    const currentTime = Date.now()
    const sessionDuration = currentTime - this.sessionStartTime

    this.totalIdleTime += MetricsTimeWindow

    if (this.totalIdleTime > MaxInactivityTime) {
      this.setUserActive(false)
    } else {
      if (type === 'widget') this.pushWidgetMetric('session_duration', MetricType.Max, this.getSessionTime())
      else if (type === 'general') this.pushMetric('session_duration', MetricType.Max, this.getSessionTime())
    }
  }

  getSessionTime() {
    const currentTime = Date.now()
    const sessionDuration = currentTime - this.sessionStartTime
    const sessionTime = Math.round(sessionDuration / 1000)
    return sessionTime
  }

  saveHeadsupAttributes() {
    if (!this.currentHeadsup) return

    this.headsupMetrics.pop()

    this.currentHeadsup['engagement_end_session_time'] = this.getSessionTime()
    this.headsupMetrics.push(this.currentHeadsup)
    logger.debug(`Headsup metrics: ${this.headsupMetrics}`)
    this.setCookie('headsupMetrics', JSON.stringify(this.headsupMetrics), CookieExpiration, false)

    this.currentHeadsup = null
  }

  sendOverdueMetrics() {
    let headsupMetrics = this.headsupMetrics
    if (headsupMetrics.length === 0) return
    let newestHeadsupMetric = headsupMetrics.sort((a, b) => new Date(b.engagement_end_session_time) - new Date(a.engagement_end_session_time))[0]
    if (newestHeadsupMetric.engagement_end_session_time === newestHeadsupMetric.engagement_start_session_time) {
      this.currentHeadsup = newestHeadsupMetric
    } else {
      return
    }

    newestHeadsupMetric.engagement_end_session_time = this.getSessionTime()
    this.setCookie('headsupMetrics', JSON.stringify(headsupMetrics), CookieExpiration, false)

    //get cached widget metrics
    let measures = this.widgetMetrics

    //if (Object.keys(measures).length === 0)
    //  return;

    let metricObject = {
      account_id: this.accountId,
      widget_id: this.widgetId,
      visitor_id: this.visitorId,
      available_channels: this.availableChannels,
      engagement_usage: this.headsupMetrics,
      activity_history: [
        {
          timestamp: new Date().toISOString(),
          url: window.location.origin + window.location.pathname,
          ml_when_model_name: this.mlWhenModel,
          ml_what_model_name: this.mlWhatModel,
          sentence_hash: this.mlSentenceHash,
          measures: measures,
        },
      ],
    }

    this.addVisitorProfile('widget')

    metricObject.activity_history = this.buildHistoryObject(metricObject)

    this.sendMetrics(metricObject, () => {}).then((resp) => {
      console.info(resp)
      this.widgetMetrics = {}
    })
  }

  // Start collecting metrics
  startCollectingMetrics() {
    this.sendOverdueMetrics()

    this.resetMetrics()
    this.attachEventListeners()
    this.attachWidgetEventListeners()

    let pageReturns = this.getPageReturns(CookieExpiration, true)
    let pageReplays = this.getPageReplays()
    let totalVisitTime = this.measureTotalVisitTime()

    logger.debug(`URL: ${window.location.href}, Page returns: ${pageReturns}, Page replays: ${pageReplays}`)
    logger.debug(`URL: ${window.location.href}, Total visit time: ${totalVisitTime} seconds`)
    this.pushMetric('page_returns', MetricType.Max, pageReturns)
    this.pushMetric('page_replays', MetricType.Max, pageReplays)

    // Set the interval to update metrics without clearing
    setInterval(() => {
      this.updateSessionDuration()

      this.calculateCurrentMetrics()

      this.processMetrics(null, 'general', true)

      this.runIntervalListeners()

      this.fixationDetector.resetData()
      this.scrollFixationDetector.resetData()
    }, MetricsTimeWindow)

    const SessionStatsInterval = 10000

    setInterval(() => {
      this.sendVisitorSessionTime()
    }, SessionStatsInterval)

    this.fixationDetector.listenToMouseEvents()
    this.scrollFixationDetector.listenToScrollEvents()
  }

  runIntervalListeners(afterWidgetInteraction = false) {
    if (!afterWidgetInteraction && this.widgetInteraction) return

    if (afterWidgetInteraction || this.intervalListenerCounter === 0) {
      for (let callback of this.intervalListeners) {
        logger.debug('Running interval listener')
        callback()
      }

      if (!afterWidgetInteraction) {
        this.intervalListenerCounter++
      }
    }
  }

  addIntervalListener(callback) {
    this.intervalListeners.push(callback)
  }

  async getInstructions(mlWhenProbe, mlWhatProbe, widget_language, aiWhen, aiWhat) {
    this.calculateCurrentMetrics(false)
    this.pushMetric('language', MetricType.Single, widget_language)
    const currentTime = Date.now()
    const sessionDuration = Math.round((currentTime - this.sessionStartTime) / 1000)
    this.pushMetric('session_duration', MetricType.Max, sessionDuration)

    let measures = this.metrics

    let metricObject = {
      account_id: this.accountId,
      widget_id: this.widgetId,
      available_channels: this.availableChannels,
      timestamp: new Date().toISOString(),
      url: window.location.origin + window.location.pathname,
      ml_when: aiWhen,
      ml_what: aiWhat,
      engagement_usage: this.headsupMetrics,
      activity_history: [
        {
          timestamp: new Date().toISOString(),
          url: window.location.origin + window.location.pathname,
          ml_when_model_name: this.mlWhenModel,
          ml_what_model_name: this.mlWhatModel,
          sentence_hash: this.mlSentenceHash,
          measures: measures,
        },
      ],
    }

    this.addVisitorProfile()

    metricObject.activity_history = this.buildHistoryObject(metricObject, false)

    let instructions = await this.sendInstructionRequest(metricObject, () => {})

    if (mlWhenProbe === true || aiWhen !== 'none') {
      this.mlWhenModel = instructions.ml_when_model_name
    }
    if (mlWhatProbe === true || aiWhat !== 'none') {
      this.mlWhatModel = instructions.ml_what_model_name
      this.mlSentenceHash = instructions.what_sentence_hash
    }

    let headsupShowCount = parseInt(this.readCookie('headsupShowCount') || 0)

    return { aiInstructions: instructions, currentSessionDuration: sessionDuration, headsupShowCount }
  }

  buildHistoryObject(metricObject, dequeue = true) {
    let activityHistory = [metricObject.activity_history[0]]
    if (dequeue) {
      while (!this.widgetMetricsQueue.isEmpty) {
        activityHistory.push(this.widgetMetricsQueue.dequeue())
      }
    } else {
      Object.values(this.widgetMetricsQueue.elements).forEach((metric) => {
        activityHistory.push(metric)
      })
    }
    activityHistory = activityHistory.sort(function (a, b) {
      return new Date(b.timestamp) - new Date(a.timestamp)
    })
    return activityHistory
  }

  addVisitorProfile(type = 'general') {
    function getDayPeriod(hour) {
      if (hour >= 4 && hour < 12) {
        return 'morning'
      } else if (hour >= 12 && hour < 20) {
        return 'afternoon'
      } else {
        return 'night'
      }
    }

    let visitorSessionId = this.readCookie('visitorSessionId') || ''

    if (type === 'general') {
      this.pushMetric('day_period', MetricType.Max, getDayPeriod(new Date().getHours()))
      this.pushMetric('device_type', MetricType.Max, this.visitorDevice)
      this.pushMetric('visitor_language', MetricType.Max, navigator.language)
      this.pushMetric('language', MetricType.Single, this.widgetLanguage)

      this.pushMetric('country', MetricType.Max, this.visitorCountry)
      this.pushMetric('referrer', MetricType.Max, document.referrer)
      this.pushMetric('visitor_session_id', MetricType.Single, visitorSessionId)
    } else if (type === 'widget') {
      this.pushWidgetMetric('day_period', MetricType.Max, getDayPeriod(new Date().getHours()))
      this.pushWidgetMetric('device_type', MetricType.Max, this.visitorDevice)
      this.pushWidgetMetric('visitor_language', MetricType.Max, navigator.language)
      this.pushWidgetMetric('language', MetricType.Single, this.widgetLanguage)

      this.pushWidgetMetric('country', MetricType.Max, this.visitorCountry)
      this.pushWidgetMetric('referrer', MetricType.Max, document.referrer)
      this.pushWidgetMetric('visitor_session_id', MetricType.Single, visitorSessionId)
    }
  }

  async sendVisitorSessionId() {
    const url = `${this.host}/api/visitor_activity/visitor_session`

    const payload = {
      timestamp: new Date().toISOString(),
      account_id: this.accountId,
      widget_id: this.widgetId,
      visitor_id: this.visitorId,
      visitor_session_id: this.visitorSessionId,
      ml_when_model_name: this.mlWhenModel || 'customer',
      ml_what_model_name: this.mlWhatModel || 'customer',
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify(payload),
      })

      return await response.json()
    } catch (error) {
      logger.warn(`Error sending visitor session ID: ${error}`)
      return null
    }
  }

  async sendVisitorSessionTime() {
    if (!this.userActive) {
      return
    }
    if (!this.sessionProbingFlag) {
      return
    }
    const url = `${this.statisticsHost}/session-stats`
    const visitorSessionId = this.readCookie('visitorSessionId')

    if (!visitorSessionId) {
      return
    }

    const payload = {
      visitor_session_id: visitorSessionId,
      session_time: this.getSessionTime(),
      device_type: this.visitorDevice,
      idle_time: this.totalIdleTime,
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify(payload),
      })
      return await response.json()
    } catch (error) {
      logger.warn(`Error sending visitor session time: ${error}`)
      return null
    }
  }

  async sendInstructionRequest(params, callback) {
    const url = `${this.host}/api/te_ai/`

    return await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify(params),
    }).then((response) => response.json())
  }

  async sendMetrics(params, callback) {
    const url = `${this.host}/api/visitor_activity/`
    this.firstPeriod = false

    return await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      },
      body: JSON.stringify(params),
    }).then((response) => response.json())
  }

  // Helper method to read the raw combined/decoded cookie value, handling chunks
  _readRawCookie(baseName) {
    const chunkPrefix = baseName + '_chunk_'
    let chunks = {}
    let foundChunks = false
    let singleValue = null

    const cookies = document.cookie.split(';')
    for (let cookie of cookies) {
      cookie = cookie.trim()
      const eqPos = cookie.indexOf('=')
      if (eqPos === -1) continue // Skip cookies without '='

      const cookieName = cookie.substring(0, eqPos)
      const cookieValue = cookie.substring(eqPos + 1)

      if (cookieName.startsWith(chunkPrefix)) {
        const indexPart = cookieName.substring(chunkPrefix.length)
        const chunkIndex = parseInt(indexPart, 10)
        if (!isNaN(chunkIndex)) {
          chunks[chunkIndex] = cookieValue
          foundChunks = true
        }
      } else if (cookieName === baseName) {
        // Found the non-chunked version
        singleValue = cookieValue
      }
    }

    let encodedValue = null

    if (foundChunks) {
      // Sort chunk keys numerically and join values
      const sortedKeys = Object.keys(chunks)
        .map(Number)
        .sort((a, b) => a - b)
      encodedValue = sortedKeys.map((key) => chunks[key]).join('')
    } else if (singleValue !== null) {
      // Use the non-chunked value if found and no chunks were found
      encodedValue = singleValue
    } else {
      // Neither chunks nor single value found
      return null
    }

    if (encodedValue === null || encodedValue === '') return null

    try {
      // Return the combined but still encoded value
      return decodeURI(encodedValue)
    } catch (e) {
      logger.error(`Error decoding raw cookie ${baseName}:`, e)
      return null // Corrupted cookie?
    }
  }

  readCookie(name, singleCookie = true) {
    let baseName
    let keyName = `TE_${name}` // Only used if singleCookie is true

    if (singleCookie) {
      baseName = 'TE_measures'
    } else {
      baseName = `TE_${name}`
    }

    const chunkPrefix = baseName + '_chunk_'
    let chunks = {}
    let foundChunks = false
    let singleValue = null // To store value if found as non-chunked

    const cookies = document.cookie.split(';')
    for (let cookie of cookies) {
      cookie = cookie.trim()
      const eqPos = cookie.indexOf('=')
      if (eqPos === -1) continue // Skip cookies without '='

      const cookieName = cookie.substring(0, eqPos)
      let cookieValue = ''
      // Handle case where cookie value might be empty
      if (eqPos < cookie.length - 1) {
        cookieValue = cookie.substring(eqPos + 1)
      }

      if (cookieName.startsWith(chunkPrefix)) {
        const indexPart = cookieName.substring(chunkPrefix.length)
        const chunkIndex = parseInt(indexPart, 10)
        if (!isNaN(chunkIndex)) {
          // Store the raw, potentially encoded value
          chunks[chunkIndex] = cookieValue
          foundChunks = true
        }
      } else if (cookieName === baseName) {
        // Found the non-chunked version
        singleValue = cookieValue
      }
    }

    let encodedValue = null

    if (foundChunks) {
      // Sort chunk keys numerically and join values
      const sortedKeys = Object.keys(chunks)
        .map(Number)
        .sort((a, b) => a - b)
      encodedValue = sortedKeys.map((key) => chunks[key]).join('')
      // logger.debug(`Read ${sortedKeys.length} chunks for ${baseName}`);
    } else if (singleValue !== null) {
      // Use the non-chunked value if found and no chunks were found
      encodedValue = singleValue
      // logger.debug(`Read single cookie ${baseName}`);
    } else {
      // Neither chunks nor single value found
      // logger.debug(`No cookie found for ${baseName}`);
      return null
    }

    if (encodedValue === null || encodedValue === '') {
      // logger.debug(`Encoded value is null or empty for ${baseName}`);
      return null
    }

    let decodedValue
    try {
      decodedValue = decodeURI(encodedValue)
    } catch (e) {
      logger.error(`Error decoding cookie ${baseName}:`, e, 'Encoded value:', encodedValue)
      return null // Corrupted cookie?
    }

    if (singleCookie) {
      let parsed = null
      try {
        // Handle empty string case after decoding
        if (decodedValue === '') {
          parsed = {}
        } else {
          parsed = JSON.parse(decodedValue)
        }
      } catch (e) {
        logger.error(`Error parsing single cookie JSON for ${baseName}:`, e, 'Decoded value:', decodedValue)
        return null // Invalid JSON
      }
      // logger.debug(`Parsed ${baseName}, returning key ${keyName}:`, parsed ? parsed[keyName] : null);
      return parsed ? parsed[keyName] || null : null
    } else {
      // For non-single cookies, return the raw decoded value
      // logger.debug(`Returning decoded value for non-single ${baseName}:`, decodedValue);
      return decodedValue
    }
  }

  setCookie(name, value, expirationInMinutes, singleCookie = true, category = 'statistics') {
    if (!cookieConsentManager.hasConsent(category)) {
      logger.debug(`No consent for category ${category}, skipping setCookie for ${name}`)
      return false // Indicate failure due to consent
    }

    const maxChunkSize = 3500 // Max size per chunk (bytes)
    let baseCookieNameToWrite
    let valueToStore
    let keyName = `TE_${name}` // Used only if singleCookie is true

    // --- Determine Base Name and Value to Store ---
    if (singleCookie) {
      baseCookieNameToWrite = 'TE_measures'
      // Read the potentially chunked, combined, decoded raw string value
      let fullMeasuresCookieValue = this._readRawCookie(baseCookieNameToWrite)
      let parsed = {}
      if (fullMeasuresCookieValue) {
        try {
          // Handle empty string case after decoding
          if (fullMeasuresCookieValue !== '') {
            parsed = JSON.parse(fullMeasuresCookieValue)
          }
        } catch (e) {
          logger.warn(`Could not parse existing TE_measures cookie in setCookie. Starting fresh. Error: ${e}. Value: ${fullMeasuresCookieValue}`)
          parsed = {}
        }
      }
      if (!parsed) parsed = {} // Ensure parsed is an object

      // Ensure visitorSessionId is present (as in original logic)
      if (!parsed.hasOwnProperty('TE_visitorSessionId') || !parsed['TE_visitorSessionId']) {
        if (this.visitorSessionId) {
          parsed['TE_visitorSessionId'] = this.visitorSessionId
        } else {
          logger.warn('this.visitorSessionId not available when setting TE_measures cookie')
        }
      }

      // Update the specific key
      parsed[keyName] = value
      valueToStore = parsed
      // logger.debug(`Updating ${keyName} in ${baseCookieNameToWrite}. New value:`, valueToStore);
    } else {
      baseCookieNameToWrite = `TE_${name}`
      valueToStore = value
      // logger.debug(`Setting non-single cookie ${baseCookieNameToWrite} to:`, valueToStore);
    }

    // --- Stringify, Encode, Calculate Expiry ---
    let stringValue
    try {
      // Ensure we don't stringify null/undefined if valueToStore is meant to be simple
      if (valueToStore === null || valueToStore === undefined) {
        stringValue = '' // Represent null/undefined as empty string in cookie? Or handle differently?
        // Let's stringify JSON null or let encodeURI handle simple undefined.
        // JSON.stringify correctly handles null. For simple values, just use them.
        if (singleCookie) {
          stringValue = JSON.stringify(valueToStore) // Handles null correctly
        } else {
          stringValue = String(valueToStore) // Coerce simple types to string
        }
      } else if (typeof valueToStore === 'object') {
        stringValue = JSON.stringify(valueToStore)
      } else {
        stringValue = String(valueToStore) // Ensure non-objects are strings
      }
    } catch (e) {
      logger.error(`Error stringifying value for cookie ${baseCookieNameToWrite}:`, e)
      return false
    }

    let encodedValue
    try {
      // encodeURI handles strings. It does not encode: ~!@#$&*()=:/,;?+'
      // For broader encoding, perhaps encodeURIComponent? Let's stick to encodeURI as requested.
      encodedValue = encodeURI(stringValue)
    } catch (e) {
      logger.error(`Error encoding value for cookie ${baseCookieNameToWrite}:`, e)
      return false
    }

    const now = new Date()
    now.setTime(now.getTime() + expirationInMinutes * 60 * 1000)
    const expires = now.toUTCString() // Use toUTCString for expires attribute
    const cookieOptions = `;expires=${expires};path=/;SameSite=Lax` // Added SameSite=Lax for security

    // --- Delete Old Cookies (Base and Chunks) ---
    // logger.debug(`Deleting existing cookies for ${baseCookieNameToWrite}`);
    const cookies = document.cookie.split(';')
    for (let cookie of cookies) {
      const eqPos = cookie.indexOf('=')
      const cookieName = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim()
      if (cookieName === baseCookieNameToWrite || cookieName.startsWith(baseCookieNameToWrite + '_chunk_')) {
        // Set expiry to the past to delete
        document.cookie = cookieName + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax'
        // logger.debug(`Deleted cookie: ${cookieName}`);
      }
    }

    // --- Write New Cookie(s) ---
    // logger.debug(`Encoded value length for ${baseCookieNameToWrite}: ${encodedValue.length}`);
    if (encodedValue.length <= maxChunkSize) {
      // Write as single cookie
      const fullCookieString = `${baseCookieNameToWrite}=${encodedValue}${cookieOptions}`
      document.cookie = fullCookieString
      // logger.debug(`Set single cookie: ${baseCookieNameToWrite}, length ${encodedValue.length}`);
      if (document.cookie.indexOf(`${baseCookieNameToWrite}=`) === -1) {
        logger.warn(`Failed to set single cookie ${baseCookieNameToWrite}. Possible size issue or browser limitation.`)
      }
    } else {
      // Write as chunks
      let chunkIndex = 0
      let totalBytesWritten = 0
      try {
        for (let i = 0; i < encodedValue.length; i += maxChunkSize) {
          const chunkValue = encodedValue.substring(i, i + maxChunkSize)
          const chunkName = `${baseCookieNameToWrite}_chunk_${chunkIndex}`
          const fullCookieString = `${chunkName}=${chunkValue}${cookieOptions}`
          document.cookie = fullCookieString
          // logger.debug(`Set chunk cookie: ${chunkName}, length ${chunkValue.length}`);
          if (document.cookie.indexOf(`${chunkName}=`) === -1) {
            logger.warn(`Failed to set chunk cookie ${chunkName}. Possible size issue or browser limitation. Stopping chunk writing.`)
            // Optionally: attempt to delete already written chunks for this set operation?
            break // Stop trying to write more chunks
          }
          totalBytesWritten += chunkValue.length // Approximation
          chunkIndex++
        }
        // logger.debug(`Set ${chunkIndex} chunks for ${baseCookieNameToWrite}, total approx length ${totalBytesWritten}`);
      } catch (e) {
        logger.error(`Error setting cookie chunk ${baseCookieNameToWrite}_chunk_${chunkIndex}: ${e}`)
        // Consider recovery or cleanup here
      }
    }

    return true // Indicate success (or at least attempted write)
  }

  uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }
}
